import { isDefined } from '@myre/common-primitives';

import { TemplateRef } from '@angular/core';
import { FormGroup, type UntypedFormArray } from '@angular/forms';
import { cloneDeep } from 'lodash-es';

import { type ElementBase } from '../elements/element-base';

import { type QuestionAlternativeInput } from './question-alternative-input';
import { QuestionBase, type IQuestionBase } from './question-base';
import { QuestionCollapseCheckbox } from './question-collapse-checkbox';
import { QuestionCollapseKpi } from './question-collapse-kpi';
import { QuestionCollapseTag } from './question-collapse-tag';
import { QuestionHidden } from './question-hidden';
import { QuestionSubtitle } from './question-subtitle';
import { QuestionTitle } from './question-title';

export interface IQuestionContainer extends Omit<
  IQuestionBase<any>,
  'initialValue' | 'defaultValue'
> {
  elements: ElementBase[];
  footerTemplate?: TemplateRef<any>;
  handleEvent?: (index: number, event: any) => void;
  /* Add a myre-toggle */
  collapsable?: boolean;
  /* default value for toggle */
  collapsableToggleValue?: boolean;
  // Display a header inside the container, with bottom border
  headerTitle?: string;
  allowToggleSwitch?: boolean;
  rememberCollapsable?: boolean;
  value$?: never;
}

export class QuestionContainer
  extends QuestionBase<any>
  implements IQuestionContainer
{
  public get controls(): FormGroup[] {
    return (this._formControl ? this._formControl.controls : []) as FormGroup[];
  }

  public get collapsableToggleValue(): boolean {
    return this._collapsableToggleValue;
  }

  public set collapsableToggleValue(value: boolean) {
    this._collapsableToggleValue = value;
  }

  public elements: ElementBase[] = [];
  public footerTemplate?: TemplateRef<any>;
  public handleEvent?: (index: number, event: any) => void;
  public collapsable: boolean;
  public headerTitle: string;
  public allowToggleSwitch: boolean;
  public rememberCollapsable: boolean;
  declare public value$?: never;

  declare protected _formControl: UntypedFormArray;
  protected _initialized: boolean = false;
  protected _collapsableToggleValue?: boolean;

  constructor(options: IQuestionContainer) {
    super(options);

    this.elementType = 'container';
    this.elements = options.elements;
    this.handleEvent = options.handleEvent;
    this.footerTemplate = options.footerTemplate;
    this.collapsable = !!options.collapsable;
    this.headerTitle = options.headerTitle;
    this.rememberCollapsable = !!options.rememberCollapsable;
    this._collapsableToggleValue = !!options.collapsableToggleValue;
    this.allowToggleSwitch = isDefined(options.allowToggleSwitch)
      ? !!options.allowToggleSwitch
      : true;

    this._setInitialValue();
  }

  public isInitialized(): boolean {
    return this._initialized;
  }

  public override setValue(): void {
    // we do not want updateValueAndValidity and _onCollectionChange to be called
    // it would trigger question changes possibly multiple times
    const updateValueAndValidityBack = this._formControl.updateValueAndValidity;
    this._formControl.updateValueAndValidity = () => {};

    // @ts-expect-error
    const _onCollectionChangeBack = this._formControl['_onCollectionChange'];

    // @ts-expect-error
    this._formControl['_onCollectionChange'] = () => {};

    if (!this._initialized) {
      this.initItem();
    }

    // now that formControl is clear, we can reset updateValueAndValidity and _onCollectionChange
    // calling them if necessary
    this._formControl.updateValueAndValidity = updateValueAndValidityBack.bind(
      this._formControl,
    );

    // @ts-expect-error
    this._formControl['_onCollectionChange'] = _onCollectionChangeBack.bind(
      this._formControl,
    );

    if (this.enabled) {
      this._formControl.updateValueAndValidity();
      // @ts-expect-error
      this._formControl['_onCollectionChange']();
    }
  }

  public override initStart(): void {
    if (this.rememberCollapsable) {
      const toggleCachedValue =
        this.questionControlService.getCachedCollapseStatus(this.key);

      if (toggleCachedValue !== null) {
        this.collapsableToggleValue = Array.isArray(toggleCachedValue)
          ? toggleCachedValue[0]
          : toggleCachedValue;
      }
    }
  }

  public initItem(): void {
    const updateValueAndValidityBack = this._formControl.updateValueAndValidity;
    this._formControl.updateValueAndValidity = () => {};

    if (!this.elements) {
      return;
    }

    // Pass the page's root form down so the children can flip its dirty
    // state on change — once rendered, Angular detaches this sub-form from
    // the root, so the children's dirty no longer bubbles up on its own.
    const form = this.questionControlService.generateForm(
      this.elements,
      this._formControl.root,
    );

    this._formControl.push(form);

    for (const element of this.elements) {
      element.getIndex = () => this._formControl.controls.indexOf(form);

      if (element instanceof QuestionBase) {
        element.setValue(
          cloneDeep(
            element.initialValue !== undefined
              ? element.initialValue
              : element.defaultValue,
          ),
        );
      }
    }

    this._initialized = true;

    this._formControl.updateValueAndValidity = updateValueAndValidityBack.bind(
      this._formControl,
    );

    if (this.enabled) {
      this._formControl.updateValueAndValidity();
    }
  }

  public override getValue(keepHidden: boolean = true): any {
    return this.questionControlService.toValues(this.elements, keepHidden);
  }

  public getLabelValue(): string | null {
    if (!this.elements) {
      return null;
    }

    const title = this.elements.find(
      element => element instanceof QuestionTitle,
    );

    return title?.getValue() ?? null;
  }

  public getSubLabelValue(): string | null {
    if (!this.elements) {
      return null;
    }

    const title = this.elements.find(
      element => element instanceof QuestionSubtitle,
    );

    return title?.getValue() ?? null;
  }

  public getCollapseTag(): QuestionCollapseTag | null {
    if (!this.elements) {
      return null;
    }

    const element = this.elements.find(e => e instanceof QuestionCollapseTag);

    return element || null;
  }

  public getCollapseTagValue(): string | null {
    const tag = this.getCollapseTag();

    return tag?.getValue() ?? null;
  }

  public getCollapseCheckbox(): QuestionCollapseCheckbox | null {
    if (!this.elements) {
      return null;
    }

    const element =
      this.elements.find(q => q instanceof QuestionCollapseCheckbox) || null;

    return element;
  }

  public getCollapseKpi(): QuestionCollapseKpi {
    if (!this.elements) {
      return null;
    }

    return this.elements.find(q => q instanceof QuestionCollapseKpi);
  }

  public onToggleChange(on: boolean): void {
    if (typeof this.handleEvent === 'function') {
      this.handleEvent(0, { on, name: 'toggle' });
    }

    if (this.rememberCollapsable) {
      this.questionControlService.setCachedCollapseStatus(this.key, on);
    }
  }

  public override enable(): void {
    const children = this.controls;
    this._formControl.controls = [];

    super.enable();

    this._formControl.controls = children;

    this.update();
  }

  public override disable(keepAtPosition: boolean = false): void {
    const children = this.controls;
    this._formControl.controls = [];

    super.disable(keepAtPosition);

    this._formControl.controls = children;
  }

  public override getFormControl(): UntypedFormArray {
    return this._formControl;
  }

  public override equals(value: any): boolean {
    if (!value) {
      return false;
    }

    const questions = this.elements.filter(
      element =>
        element instanceof QuestionBase &&
        !(element instanceof QuestionHidden) &&
        !element.excludeFromControls,
    ) as QuestionBase<any>[];

    return !questions.some(q => {
      let compareValue = value[q.key];

      if (q.elementType === 'alternativeInput') {
        const altInput = q as unknown as QuestionAlternativeInput;
        const { key, secondaryOutputKey } = altInput;

        compareValue = {
          [key]: value[key],
          [secondaryOutputKey]: value[secondaryOutputKey],
        };
      }

      return !q.equals(compareValue);
    });
  }

  private _setInitialValue(): void {
    const elements = this.elements || [];
    this.initialValue = elements
      .map(
        e =>
          e instanceof QuestionBase && {
            key: e.key,
            value: e.getInitialValue(),
          },
      )
      .reduce((acc, item) => {
        if (item.key) {
          // @ts-expect-error
          acc[item.key] = item.value;
        }

        return acc;
      }, {});

    for (const questionAlternativeInput of this._getQuestionAlternativeInputSelectedSecondaryElements(
      elements,
    )) {
      const { secondaryOutputKey, selectedSecondaryElement } =
        questionAlternativeInput;
      this.initialValue[secondaryOutputKey] =
        selectedSecondaryElement.getInitialValue();
    }
  }

  private _getQuestionAlternativeInputSelectedSecondaryElements(
    elements: readonly ElementBase<any>[],
  ): QuestionAlternativeInput<unknown>[] {
    return elements.filter(
      element =>
        element instanceof QuestionBase &&
        element.elementType === 'alternativeInput',
    ) as unknown as QuestionAlternativeInput<unknown>[];
  }
}
